import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApolloError } from '@apollo/client';
import { Menu, MenuItem } from '@mui/material';

import {
  IconButton,
  LinearProgress,
  LoadedContent,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  MoreVertIcon,
} from '@occc/ui-design-system';
import { RetailerNetworkLocationGroupFragment } from '@occc/graphql';
import { IPaginationProps } from '@occc-app/hooks/usePaginatedQuery';
import { Can } from '@occc-app/App/PolicyBasedAccessControl';
import {
  RemoveRetailerNetworkLocationGroups,
  UpdateRetailerNetworkLocationGroups,
} from '@occc-app/App/PolicyBasedAccessControl/userActions';

import { AFTER_PARAM_NAME } from './retailer-network-location-groups.constants';

import { routeCreator } from '../../retailer-connect.utils';

type MenuItem = 'renameLocationGroup' | 'removeLocationGroup' | 'viewLocationGroupDetails';

type LocationGroupsTableProps = {
  retailerNetworkUuid: string;
  locationGroups: RetailerNetworkLocationGroupFragment[] | undefined | null;
  loading: boolean;
  error?: ApolloError;
  paginationProps: IPaginationProps;
  onEdit: (group: RetailerNetworkLocationGroupFragment) => void;
  onDelete: (group: RetailerNetworkLocationGroupFragment) => void;
};

export const LocationGroupsTable = ({
  retailerNetworkUuid,
  locationGroups,
  loading,
  error,
  paginationProps,
  onEdit,
  onDelete,
}: LocationGroupsTableProps) => {
  const navigate = useNavigate();
  const [actionsForUuid, setActionsForUuid] = useState<string | null>();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const getHandlerForAdditionalActions =
    (group: RetailerNetworkLocationGroupFragment) => (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();

      setAnchorEl(event.currentTarget);
      setActionsForUuid(group.uuid);
    };

  const handleClose = React.useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(null);
    setActionsForUuid(null);
  }, []);

  const onNavigateToDetails = useCallback(
    (uuid: string) => {
      navigate(routeCreator.locationGroupDetails(retailerNetworkUuid, uuid));
    },
    [navigate, retailerNetworkUuid],
  );

  const onMenuNavigationClick =
    (item?: RetailerNetworkLocationGroupFragment, itemName?: MenuItem) =>
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();

      if (itemName === 'viewLocationGroupDetails' && item) {
        onNavigateToDetails(item.uuid);
      }

      if (itemName === 'renameLocationGroup' && item) {
        onEdit(item);
      }

      if (itemName === 'removeLocationGroup' && item) {
        onDelete(item);
      }

      handleClose(event);
    };

  const onTableRowClick = useCallback(
    (event: React.MouseEvent<HTMLTableRowElement>, uuid: string): void => {
      event.stopPropagation();
      onNavigateToDetails(uuid);
    },
    [onNavigateToDetails],
  );

  const renderData = (items: RetailerNetworkLocationGroupFragment[]) => {
    return (
      <TableContainer sx={{ borderRadius: 0 }}>
        {loading && <LinearProgress />}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Group Name</TableCell>
              <TableCell align='right'>Locations</TableCell>
              <TableCell align='right'>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow
                key={item.uuid}
                sx={{ cursor: 'pointer' }}
                onClick={(event) => onTableRowClick(event, item.uuid)}
              >
                <TableCell>{item.name}</TableCell>
                <TableCell align='right'>{item.countOfLocations}</TableCell>
                <TableCell align='right' width='10%'>
                  <IconButton onClick={getHandlerForAdditionalActions(item)} sx={{ p: 0 }}>
                    <MoreVertIcon />
                  </IconButton>
                  <Menu
                    open={item.uuid === actionsForUuid}
                    onClose={handleClose}
                    anchorEl={anchorEl}
                    PaperProps={{
                      sx: {
                        borderRadius: '4px',
                      },
                    }}
                  >
                    <MenuItem onClick={onMenuNavigationClick(item, 'viewLocationGroupDetails')}>
                      View Location Group Details
                    </MenuItem>

                    <Can I={UpdateRetailerNetworkLocationGroups({ uuid: retailerNetworkUuid })}>
                      <MenuItem onClick={onMenuNavigationClick(item, 'renameLocationGroup')}>
                        Rename
                      </MenuItem>
                    </Can>

                    <Can I={RemoveRetailerNetworkLocationGroups({ uuid: retailerNetworkUuid })}>
                      <MenuItem onClick={onMenuNavigationClick(item, 'removeLocationGroup')}>
                        Delete
                      </MenuItem>
                    </Can>
                  </Menu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Pagination {...paginationProps} />
      </TableContainer>
    );
  };

  return (
    <LoadedContent<RetailerNetworkLocationGroupFragment>
      error={error}
      loading={loading}
      items={locationGroups}
      renderData={renderData}
      afterParamName={AFTER_PARAM_NAME}
    />
  );
};
